import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import {
  PROVIDER_CODES,
  PROVIDER_DEFAULT_BASE_URL,
  PROVIDER_LABEL,
  SUGGESTED_MODELS,
  type ProviderCode,
} from '@ambec/shared';
import { ApiError, apiFetch } from '../../../lib/api';
import { Modal } from '../../../components/Modal';
import {
  inputClass,
  labelClass,
  type LlmProvider,
  type TestResult,
} from './tipos';

function traducirError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Ocurrió un error inesperado. Inténtalo de nuevo.';
}

function ChipEstado({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
        enabled
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-300'
          : 'bg-slate-100 text-slate-600 ring-slate-300'
      }`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-slate-400'}`}
      />
      {enabled ? 'Habilitado' : 'Deshabilitado'}
    </span>
  );
}

function ChipSensibilidad({ tag }: { tag: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-azul-noche/5 px-2.5 py-0.5 text-[11px] font-medium text-azul-noche ring-1 ring-inset ring-azul-noche/25">
      {tag}
    </span>
  );
}

// ─────────────────────────────── Formulario ────────────────────────────────

function ConectorForm({
  conector,
  onCerrar,
  onGuardado,
}: {
  conector: LlmProvider | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esEdicion = conector !== null;
  const [code, setCode] = useState<ProviderCode>(conector?.code ?? PROVIDER_CODES[0]);
  const [name, setName] = useState(conector?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(
    conector?.base_url ?? PROVIDER_DEFAULT_BASE_URL[PROVIDER_CODES[0]],
  );
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(conector?.model ?? '');
  const [modelos, setModelos] = useState<string[]>([]);
  const [leyendoModelos, setLeyendoModelos] = useState(false);
  const [errorModelos, setErrorModelos] = useState<string | null>(null);
  const [sensitivity, setSensitivity] = useState(conector?.sensitivity_tag ?? '');
  const [enabled, setEnabled] = useState(conector?.enabled ?? true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeId = useId();
  const nameId = useId();
  const baseId = useId();
  const keyId = useId();
  const modelId = useId();
  const modelListId = useId();
  const sensId = useId();
  const enabledId = useId();

  /** Lee los modelos disponibles del proveedor (key tipeada o la guardada del conector). */
  async function onLeerModelos() {
    setErrorModelos(null);
    setLeyendoModelos(true);
    try {
      const data = await apiFetch<{ models: string[] }>('/api/admin/llm-providers/models', {
        method: 'POST',
        body: JSON.stringify({
          code,
          api_key: apiKey.trim() || undefined,
          base_url: baseUrl.trim() || undefined,
          provider_id: conector?.id,
        }),
      });
      setModelos(data.models);
      if (data.models.length === 0) setErrorModelos('El proveedor no devolvió modelos.');
    } catch (err) {
      setErrorModelos(traducirError(err));
    } finally {
      setLeyendoModelos(false);
    }
  }

  function onCambiarProveedor(nuevo: ProviderCode) {
    setCode(nuevo);
    // Prellena la URL base sugerida solo si el usuario no la personalizó.
    if (!baseUrl || Object.values(PROVIDER_DEFAULT_BASE_URL).includes(baseUrl)) {
      setBaseUrl(PROVIDER_DEFAULT_BASE_URL[nuevo]);
    }
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (enviando) return;
    setError(null);
    setEnviando(true);
    try {
      if (esEdicion) {
        const body: Record<string, unknown> = {
          name: name.trim(),
          base_url: baseUrl.trim() || null,
          model: model.trim() || null,
          sensitivity_tag: sensitivity.trim() || null,
          enabled,
        };
        if (apiKey.trim()) body.api_key = apiKey;
        await apiFetch(`/api/admin/llm-providers/${conector.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch('/api/admin/llm-providers', {
          method: 'POST',
          body: JSON.stringify({
            code,
            name: name.trim(),
            base_url: baseUrl.trim() || undefined,
            api_key: apiKey,
            model: model.trim() || undefined,
            sensitivity_tag: sensitivity.trim() || undefined,
            enabled,
          }),
        });
      }
      onGuardado();
    } catch (err) {
      setError(traducirError(err));
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      {error && (
        <div
          role="alert"
          className="alerta-error"
        >
          {error}
        </div>
      )}

      <div>
        <label htmlFor={codeId} className={labelClass}>
          Proveedor
        </label>
        <select
          id={codeId}
          value={code}
          disabled={esEdicion}
          onChange={(e) => onCambiarProveedor(e.target.value as ProviderCode)}
          className={inputClass}
        >
          {PROVIDER_CODES.map((c) => (
            <option key={c} value={c}>
              {PROVIDER_LABEL[c]}
            </option>
          ))}
        </select>
        {esEdicion && (
          <p className="mt-1.5 text-xs font-light text-gris">
            El proveedor no puede modificarse.
          </p>
        )}
      </div>

      <div>
        <label htmlFor={nameId} className={labelClass}>
          Nombre
        </label>
        <input
          id={nameId}
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: Anthropic producción"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={baseId} className={labelClass}>
          URL base
        </label>
        <input
          id={baseId}
          type="url"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://…"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={keyId} className={labelClass}>
          {esEdicion ? 'Reemplazar API key' : 'API key'}
        </label>
        <input
          id={keyId}
          type="password"
          required={!esEdicion}
          value={apiKey}
          autoComplete="off"
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={esEdicion ? 'Dejar en blanco para no cambiarla' : 'Pega la clave del proveedor'}
          className={inputClass}
        />
        {esEdicion && conector && (
          <p className="mt-1.5 text-xs font-light text-gris">
            Clave actual: <span className="font-medium text-texto">{conector.api_key_hint}</span>
          </p>
        )}
      </div>

      <div>
        <label htmlFor={modelId} className={labelClass}>
          Modelo
        </label>
        <div className="flex gap-2">
          <input
            id={modelId}
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={SUGGESTED_MODELS[code]?.[0] ?? 'nombre-del-modelo'}
            list={modelListId}
            className="campo"
          />
          <button
            type="button"
            onClick={() => void onLeerModelos()}
            disabled={leyendoModelos}
            className="btn-secundario h-11 shrink-0 whitespace-nowrap"
          >
            {leyendoModelos ? 'Leyendo…' : 'Leer modelos'}
          </button>
        </div>
        <datalist id={modelListId}>
          {(modelos.length > 0 ? modelos : (SUGGESTED_MODELS[code] ?? [])).map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        {modelos.length > 0 && (
          <select
            aria-label="Modelos disponibles del proveedor"
            value={modelos.includes(model) ? model : ''}
            onChange={(e) => setModel(e.target.value)}
            className="campo mt-2"
          >
            <option value="" disabled>
              — {modelos.length} modelos disponibles: elige uno —
            </option>
            {modelos.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}
        {errorModelos && (
          <p role="alert" className="mt-1.5 text-xs font-normal text-red-600">
            {errorModelos}
          </p>
        )}
        <p className="mt-1.5 text-xs font-light text-gris">
          Cada conexión fija un modelo: puedes crear varias del mismo proveedor con
          modelos distintos.
        </p>
      </div>

      <div>
        <label htmlFor={sensId} className={labelClass}>
          Etiqueta de sensibilidad (opcional)
        </label>
        <input
          id={sensId}
          type="text"
          value={sensitivity}
          onChange={(e) => setSensitivity(e.target.value)}
          placeholder="Ej: confidencial, interno"
          className={inputClass}
        />
      </div>

      <label htmlFor={enabledId} className="flex items-center gap-3">
        <input
          id={enabledId}
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-azul-profundo focus:ring-azul-profundo"
        />
        <span className="text-sm font-normal text-texto">Conector habilitado</span>
      </label>

      <div className="mt-2 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCerrar}
          className="btn-fantasma"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={enviando}
          className="btn-primario"
        >
          {enviando ? 'Guardando…' : esEdicion ? 'Guardar cambios' : 'Crear conector'}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────── Confirmación de eliminación ───────────────────────

function ConfirmarEliminar({
  conector,
  onCerrar,
  onEliminado,
}: {
  conector: LlmProvider;
  onCerrar: () => void;
  onEliminado: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onEliminar() {
    setError(null);
    setEnviando(true);
    try {
      await apiFetch(`/api/admin/llm-providers/${conector.id}`, { method: 'DELETE' });
      onEliminado();
    } catch (err) {
      setError(traducirError(err));
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Eliminar conector" onCerrar={onCerrar}>
      {error && (
        <div
          role="alert"
          className="alerta-error mb-5"
        >
          {error}
        </div>
      )}
      <p className="text-sm font-light leading-relaxed text-texto">
        ¿Seguro que quieres eliminar el conector{' '}
        <span className="font-medium text-azul-profundo">{conector.name}</span>? Los agentes que
        lo usen quedarán sin proveedor asignado.
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCerrar}
          className="btn-fantasma"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onEliminar}
          disabled={enviando}
          className="btn-peligro"
        >
          {enviando ? 'Eliminando…' : 'Eliminar'}
        </button>
      </div>
    </Modal>
  );
}

// ──────────────────────────────── Sección ──────────────────────────────────

type Dialogo =
  | { tipo: 'crear' }
  | { tipo: 'editar'; conector: LlmProvider }
  | { tipo: 'eliminar'; conector: LlmProvider }
  | null;

export default function SeccionConectores({ onCambio }: { onCambio?: () => void }) {
  const [conectores, setConectores] = useState<LlmProvider[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [alternando, setAlternando] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const [probando, setProbando] = useState<string | null>(null);
  const [resultados, setResultados] = useState<Record<string, TestResult | { error: string }>>({});

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga(null);
    try {
      const data = await apiFetch<{ providers: LlmProvider[] }>('/api/admin/llm-providers');
      setConectores(data.providers);
    } catch (err) {
      setErrorCarga(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  function cerrarYRefrescar() {
    setDialogo(null);
    void cargar();
    onCambio?.();
  }

  async function alternarEnabled(c: LlmProvider) {
    setErrorAccion(null);
    setAlternando(c.id);
    try {
      await apiFetch(`/api/admin/llm-providers/${c.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !c.enabled }),
      });
      await cargar();
      onCambio?.();
    } catch (err) {
      setErrorAccion(traducirError(err));
    } finally {
      setAlternando(null);
    }
  }

  async function probar(c: LlmProvider) {
    setProbando(c.id);
    setResultados((prev) => {
      const { [c.id]: _omit, ...resto } = prev;
      return resto;
    });
    // Modelo del conector; si no tiene, el primero sugerido para el proveedor.
    const model = c.model ?? SUGGESTED_MODELS[c.code]?.[0];
    try {
      const res = await apiFetch<TestResult>(`/api/admin/llm-providers/${c.id}/test`, {
        method: 'POST',
        body: JSON.stringify(model ? { model } : {}),
      });
      setResultados((prev) => ({ ...prev, [c.id]: res }));
    } catch (err) {
      setResultados((prev) => ({ ...prev, [c.id]: { error: traducirError(err) } }));
    } finally {
      setProbando(null);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm font-normal text-gris">
          Conecta los proveedores de modelos de lenguaje. Las claves se cifran y nunca se muestran
          en claro.
        </p>
        <button
          type="button"
          onClick={() => setDialogo({ tipo: 'crear' })}
          className="btn-primario"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Nuevo conector
        </button>
      </div>

      {errorAccion && (
        <div
          role="alert"
          className="alerta-error"
        >
          {errorAccion}
        </div>
      )}

      {cargando ? (
        <div
          role="status"
          aria-live="polite"
          className="tarjeta px-6 py-16 text-center text-sm font-normal text-gris"
        >
          Cargando conectores…
        </div>
      ) : errorCarga ? (
        <div className="tarjeta px-6 py-16 text-center">
          <p className="text-sm text-red-700">{errorCarga}</p>
          <button
            type="button"
            onClick={() => void cargar()}
            className="btn-secundario mt-4"
          >
            Reintentar
          </button>
        </div>
      ) : conectores.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center">
          <p className="text-sm font-medium text-azul-profundo">
            Sin conectores todavía
          </p>
          <p className="mt-3 text-sm font-normal text-gris">
            Agrega tu primer proveedor con “Nuevo conector”.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {conectores.map((c) => {
            const resultado = resultados[c.id];
            return (
              <li
                key={c.id}
                className="tarjeta p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-azul-profundo">
                        {PROVIDER_LABEL[c.code]}
                      </span>
                      {c.model && (
                        <span className="chip bg-celeste/20 font-mono text-[11px] text-azul-profundo ring-celeste/60">
                          {c.model}
                        </span>
                      )}
                      <ChipEstado enabled={c.enabled} />
                      {c.sensitivity_tag && <ChipSensibilidad tag={c.sensitivity_tag} />}
                    </div>
                    <p className="mt-1.5 text-base font-normal text-texto">{c.name}</p>
                    <p className="text-xs font-light text-gris">
                      Clave {c.api_key_hint}
                      {c.base_url ? ` · ${c.base_url}` : ''}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-1">
                    <button
                      type="button"
                      onClick={() => void probar(c)}
                      disabled={probando === c.id}
                      className="btn-tabla gap-2 text-azul-profundo hover:bg-azul-profundo/[0.08]"
                    >
                      {probando === c.id && (
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          className="h-3.5 w-3.5 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        >
                          <path d="M12 3a9 9 0 1 0 9 9" />
                        </svg>
                      )}
                      {probando === c.id ? 'Probando…' : 'Probar conexión'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDialogo({ tipo: 'editar', conector: c })}
                      className="btn-tabla text-azul-profundo hover:bg-azul-profundo/[0.08]"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => void alternarEnabled(c)}
                      disabled={alternando === c.id}
                      className="btn-tabla text-gris hover:bg-slate-100 hover:text-azul-noche"
                    >
                      {alternando === c.id ? '…' : c.enabled ? 'Deshabilitar' : 'Habilitar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDialogo({ tipo: 'eliminar', conector: c })}
                      className="btn-tabla text-red-600 hover:bg-red-50 focus-visible:!outline-red-600"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>

                {resultado && (
                  <div
                    role="status"
                    className={`mt-4 rounded-lg px-4 py-2.5 text-sm ${
                      'error' in resultado
                        ? 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-200'
                        : resultado.ok
                          ? 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200'
                          : 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200'
                    }`}
                  >
                    {'error' in resultado ? (
                      resultado.error
                    ) : (
                      <>
                        {resultado.ok ? '✓ ' : '✕ '}
                        {resultado.message}
                        <span className="font-light"> · {resultado.latencyMs} ms</span>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {(dialogo?.tipo === 'crear' || dialogo?.tipo === 'editar') && (
        <Modal
          titulo={dialogo.tipo === 'crear' ? 'Nuevo conector' : 'Editar conector'}
          onCerrar={() => setDialogo(null)}
        >
          <ConectorForm
            conector={dialogo.tipo === 'editar' ? dialogo.conector : null}
            onCerrar={() => setDialogo(null)}
            onGuardado={cerrarYRefrescar}
          />
        </Modal>
      )}

      {dialogo?.tipo === 'eliminar' && (
        <ConfirmarEliminar
          conector={dialogo.conector}
          onCerrar={() => setDialogo(null)}
          onEliminado={cerrarYRefrescar}
        />
      )}
    </div>
  );
}
